<template>
  <DataTable
    :value="items"
    :class="[
      items && items.length == 0 ? 'empty-table' : '',
      isActiveFirst ? 'active-first' : '',
    ]"
    class="scroll-table-y main-table scrollbar-custom"
    stripedRows
    @sort="sortTable"
    removableSort
    scrollable
    :loading="isLoading"
    dataKey="source_segment_id"
    columnResizeMode="expand"
  >
    <Column field="number" header="№" style="min-width: 44px">
      <template #body="slotProps">
        {{ rowsCount + slotProps.index + 1 }}
      </template>
    </Column>
    <Column
      field="campaign.name"
      header="Имя кампании"
      style="min-width: 150px"
    >
      <template #body="slotProps">
        <RouterLink
          v-if="slotProps.data.campaign"
          :to="{ name: 'campaign', params: { id: slotProps.data.campaign.id } }"
        >
          {{ slotProps.data.campaign.name }}
        </RouterLink>
        <span v-else>-</span>
      </template>
    </Column>
    <Column field="segment.name" header="Имя сегмента" style="min-width: 150px">
      <template #body="slotProps">
        <RouterLink
          v-if="slotProps.data.segment"
          :to="{ name: 'segment', params: { id: slotProps.data.segment.id } }"
        >
          {{ slotProps.data.segment.name }}
        </RouterLink>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="segment_rich.rich"
      header="Охват сегмента"
      style="min-width: 120px"
    >
      <template #body="slotProps">
        <span
          v-if="slotProps.data.segment_rich && slotProps.data.segment_rich.rich"
        >
          {{ formatNumber(slotProps.data.segment_rich.rich) }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="segment_rich.cost"
      header="Стоимость сегмента"
      style="min-width: 150px"
    >
      <template #body="slotProps">
        <span
          v-if="slotProps.data.segment_rich && slotProps.data.segment_rich.cost"
        >
          {{ formatNumber(slotProps.data.segment_rich.cost / 100) }} ₽
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column field="ads_cabinet.name" header="Кабинет" style="min-width: 150px">
      <template #body="slotProps">
        <span v-if="slotProps.data.ads_cabinet">
          {{ slotProps.data.ads_cabinet.name }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="platform_segment_state.platform_slug"
      header="Площадка"
      style="min-width: 150px"
    >
      <template #body="slotProps">
        <span
          v-if="
            slotProps.data.platform_segment_state &&
            slotProps.data.platform_segment_state.platform_slug
          "
        >
          {{
            getPlatformName(slotProps.data.platform_segment_state.platform_slug)
          }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="campaign_segment_rich.rich"
      header="Количество хэшей"
      style="min-width: 180px"
    >
      <template #body="slotProps">
        <span
          v-if="
            slotProps.data.campaign_segment_rich &&
            slotProps.data.campaign_segment_rich.rich
          "
        >
          {{ formatNumber(slotProps.data.campaign_segment_rich.rich) }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="campaign_segment_state.display_name"
      header="Статус сегмента на площадке"
      style="min-width: 200px"
    >
      <template #body="slotProps">
        <span
          v-if="
            slotProps.data.campaign_segment_state &&
            slotProps.data.campaign_segment_state.display_name
          "
        >
          {{ slotProps.data.campaign_segment_state.display_name }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="source_segment_id"
      header="ID сегмента на площадке"
      style="min-width: 180px"
    >
      <template #body="slotProps">
        <span v-if="slotProps.data.source_segment_id">
          {{ slotProps.data.source_segment_id }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="ctime"
      header="Дата загрузки на площадку"
      style="min-width: 180px"
    >
      <template #body="slotProps">
        <span v-if="slotProps.data.ctime">
          {{ convertDateFromUTC(slotProps.data.ctime) }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
    <Column
      field="segment.tenant"
      header="Организация"
      style="min-width: 150px"
    >
      <template #body="slotProps">
        <span v-if="slotProps.data.segment && slotProps.data.segment.tenant">
          {{ getOrganizationName(slotProps.data.segment.tenant) }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { onMounted } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { formatNumber } from "@/services/utils/Strings";
import { convertDateFromUTC } from "@/services/utils/DateParser";
import { useSourcesStore } from "@/store/catalog_data/sources";
import { useOrganizationsBaseDictionaryStore } from "@/store/catalog_data/organization_base";

defineProps<{
  items: any[];
  isActiveFirst: boolean;
  isLoading: boolean;
  rowsCount: number;
}>();

const emits = defineEmits(["sortTable"]);

const sourcesStore = useSourcesStore();
const organizationsStore = useOrganizationsBaseDictionaryStore();

onMounted(async () => {
  if (!sourcesStore.itemList.length) {
    if (sourcesStore.isLoadingInitial && sourcesStore.initialLoadPromise) {
      await sourcesStore.initialLoadPromise;
    } else {
      await sourcesStore.getItems("", [], true, false, 0, 100, true);
    }
  }
  if (organizationsStore.itemList.length === 0) {
    if (!organizationsStore.isLoadingInitial) {
      await organizationsStore.getItems("", [], true, true, 0, 100, true);
    }
  }
});

const getPlatformName = (slug: string): string => {
  const platform = sourcesStore.itemList.find((p) => p.slug === slug);
  return platform ? platform.name : slug;
};

const getOrganizationName = (tenant: string): string => {
  const organization = organizationsStore.itemList.find(
    (o) => o.tenant === tenant
  );
  return organization ? organization.name : tenant;
};

const sortTable = (event: any) => {
  let orderField = "";
  if (event.sortField)
    orderField = event.sortOrder > 0 ? event.sortField : `-${event.sortField}`;
  emits("sortTable", orderField);
};
</script>
